'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import PageAtmosphere from '@/components/PageAtmosphere';
import { useT } from '@/lib/useT';
import {
  Briefcase, MapPin, Search, Building2, CalendarClock, Banknote,
  ChevronLeft, ChevronRight, BadgeCheck, Truck,
} from 'lucide-react';

/* ─── types — mirror /api/public/jobs response shape ──────────────── */
interface JobItem {
  id: string;
  title: string;
  description: string;
  city: string | null;
  country: string | null;
  employmentType: string | null;
  licenses: string[];
  languages: string[];
  code95: boolean;
  salary: {
    min: number | null;
    max: number | null;
    unit: string | null;
    currency: string | null;
  } | null;
  startDate: string | null;
  publishedAt: string | null;
  validThrough: string | null;
  org: {
    name: string;
    slug: string;
    logo: string | null;
    brandColor: string | null;
    country: string | null;
    city: string | null;
  };
}

interface JobsResponse {
  data: JobItem[];
  total: number;
  page: number;
  limit: number;
  countries: string[];
}

/* ─── helpers ─────────────────────────────────────────────────────── */
const FLAGS: Record<string, string> = {
  DE: '\u{1F1E9}\u{1F1EA}', AT: '\u{1F1E6}\u{1F1F9}', CH: '\u{1F1E8}\u{1F1ED}',
  PL: '\u{1F1F5}\u{1F1F1}', CZ: '\u{1F1E8}\u{1F1FF}', UA: '\u{1F1FA}\u{1F1E6}',
  RU: '\u{1F1F7}\u{1F1FA}', MD: '\u{1F1F2}\u{1F1E9}', BG: '\u{1F1E7}\u{1F1EC}',
  KZ: '\u{1F1F0}\u{1F1FF}', NL: '\u{1F1F3}\u{1F1F1}', BE: '\u{1F1E7}\u{1F1EA}',
  FR: '\u{1F1EB}\u{1F1F7}', IT: '\u{1F1EE}\u{1F1F9}', ES: '\u{1F1EA}\u{1F1F8}',
  EE: '\u{1F1EA}\u{1F1EA}', LV: '\u{1F1F1}\u{1F1FB}', LT: '\u{1F1F1}\u{1F1F9}',
  RO: '\u{1F1F7}\u{1F1F4}', HU: '\u{1F1ED}\u{1F1FA}',
};

const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY', 'INTERNSHIP'];

const RAW_API = process.env.NEXT_PUBLIC_API_URL || '';
const API = RAW_API.replace(/\/api$/, '');

function formatSalary(s: JobItem['salary'], t: (k: string) => string): string | null {
  if (!s || (s.min == null && s.max == null)) return null;
  const cur = s.currency || 'EUR';
  const unit = s.unit === 'HOURLY' ? t('jobs_salary_hr') : s.unit === 'YEARLY' ? t('jobs_salary_yr') : t('jobs_salary_mo');
  if (s.min != null && s.max != null) return `${s.min}–${s.max} ${cur} / ${unit}`;
  if (s.min != null) return `${t('jobs_salary_from')} ${s.min} ${cur} / ${unit}`;
  if (s.max != null) return `${t('jobs_salary_upto')} ${s.max} ${cur} / ${unit}`;
  return null;
}

/* ─── component ───────────────────────────────────────────────────── */
export default function JobsIndexPage() {
  const t = useT();
  const [data, setData] = useState<JobsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [page, setPage] = useState(1);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (country) params.set('country', country);
      if (city) params.set('city', city);
      if (employmentType) params.set('employmentType', employmentType);
      params.set('page', String(page));

      const res = await fetch(`${API}/api/public/jobs?${params.toString()}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch {
      // silent — fall through to empty state
    } finally {
      setLoading(false);
    }
  }, [search, country, city, employmentType, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Debounced search input → query param
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const jobs = data?.data ?? [];
  const countries = data?.countries ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  // Schema.org JobPosting structured data — Google indexes these as job-rich-results
  const jobPostingSchema = jobs.length > 0 ? {
    '@context': 'https://schema.org',
    '@graph': jobs.map((j) => ({
      '@type': 'JobPosting',
      title: j.title,
      description: j.description,
      datePosted: j.publishedAt,
      validThrough: j.validThrough || undefined,
      employmentType: j.employmentType,
      hiringOrganization: {
        '@type': 'Organization',
        name: j.org.name,
        sameAs: `https://logixa.one/company/${j.org.slug}`,
      },
      jobLocation: j.city || j.country ? {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressLocality: j.city || undefined,
          addressCountry: j.country || undefined,
        },
      } : undefined,
      baseSalary: j.salary && (j.salary.min || j.salary.max) ? {
        '@type': 'MonetaryAmount',
        currency: j.salary.currency || 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: j.salary.min || undefined,
          maxValue: j.salary.max || undefined,
          unitText: j.salary.unit || 'MONTH',
        },
      } : undefined,
    })),
  } : null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--c-bg)' }}>

      <PublicHeader />
      <PageAtmosphere />

      {/* ════════════ PAGE TITLE ════════════ */}
      <section className="relative overflow-hidden px-6 md:px-8 pt-20 pb-12 max-w-7xl mx-auto text-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--live) 10%, transparent) 0%, transparent 70%)' }} />

        <div className="relative z-10 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-wide uppercase"
            style={{ background: 'color-mix(in srgb, var(--live) 12%, transparent)', color: 'var(--live)', border: '1px solid color-mix(in srgb, var(--live) 25%, transparent)' }}>
            <Briefcase size={14} /> {t('jobs_badge')}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-ink mb-4">
            {t('jobs_title')} <span className="gradient-text">{t('jobs_title_accent')}</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: 'var(--c-text)' }}>
            {t('jobs_sub')}
          </p>
        </div>
      </section>

      {/* ════════════ FILTERS ════════════ */}
      <section className="px-6 md:px-8 pb-8 max-w-7xl mx-auto">
        <div className="glass-card p-4 md:p-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
            <input
              type="text"
              placeholder={t('jobs_search_ph')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-ink placeholder:text-[var(--c-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40"
              style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)' }}
            />
          </div>

          {/* Country */}
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 appearance-none cursor-pointer"
            style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', minWidth: 180 }}
          >
            <option value="">{t('jobs_all_countries')}</option>
            {countries.map((c) => (
              <option key={c} value={c}>{FLAGS[c] ?? ''} {c}</option>
            ))}
          </select>

          {/* City */}
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
            <input
              type="text"
              placeholder={t('jobs_city_ph')}
              value={city}
              onChange={(e) => { setCity(e.target.value); setPage(1); }}
              className="w-full md:w-44 pl-10 pr-4 py-2.5 rounded-xl text-sm text-ink placeholder:text-[var(--c-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40"
              style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)' }}
            />
          </div>

          {/* Employment type */}
          <select
            value={employmentType}
            onChange={(e) => { setEmploymentType(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 appearance-none cursor-pointer"
            style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', minWidth: 180 }}
          >
            <option value="">{t('jobs_all_types')}</option>
            {EMPLOYMENT_TYPES.map((et) => (
              <option key={et} value={et}>{t(`jobs_et_${et.toLowerCase()}`)}</option>
            ))}
          </select>
        </div>
      </section>

      {/* ════════════ JOB GRID ════════════ */}
      <section className="px-6 md:px-8 pb-12 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--c-accent)', borderTopColor: 'transparent' }} />
            <p className="mt-4 text-sm" style={{ color: 'var(--c-muted)' }}>{t('jobs_loading')}</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase size={48} className="mx-auto mb-4" style={{ color: 'var(--c-muted)' }} />
            <p className="text-lg font-display font-bold text-ink mb-2">{t('jobs_empty')}</p>
            <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('jobs_empty_hint')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 stagger-children">
              <style>{`.lv3pc{transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease}.lv3pc:hover{transform:translateY(-5px);box-shadow:0 18px 50px color-mix(in srgb, var(--accent) 12%, transparent);border-color:color-mix(in srgb, var(--accent) 40%, transparent)!important}.lv3pc:hover .lv3pc-bar{transform:scaleY(1)!important}`}</style>
              {jobs.map((j) => {
                const accent = j.org.brandColor || 'var(--c-accent)';
                const salaryText = formatSalary(j.salary, t);
                return (
                  <div key={j.id} className="lv3pc group" style={{ position: 'relative', background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 16, padding: 24, overflow: 'hidden' }}>
                    <span aria-hidden className="lv3pc-bar" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'var(--accent)', transform: 'scaleY(0)', transformOrigin: 'top', transition: 'transform .25s' }} />
                    {/* Header: org + title */}
                    <div className="flex items-start gap-3 mb-4">
                      {j.org.logo ? (
                        <img src={j.org.logo} alt={j.org.name} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: `${accent}20`, color: accent }}>
                          <Building2 size={18} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-ink font-display leading-tight line-clamp-2">
                          {j.title}
                        </h3>
                        <Link href={`/company/${j.org.slug}`}
                          className="text-xs font-medium hover:text-ink transition-colors" style={{ color: 'var(--c-muted)' }}>
                          {j.org.name}
                        </Link>
                      </div>
                    </div>

                    {/* Meta row 1: location + employment */}
                    <div className="flex flex-wrap items-center gap-3 mb-3 text-xs" style={{ color: 'var(--c-text)' }}>
                      {(j.city || j.country) && (
                        <span className="flex items-center gap-1.5">
                          <MapPin size={12} style={{ color: 'var(--c-muted)' }} />
                          <span>{FLAGS[j.country || ''] ?? ''} {j.city || j.country}</span>
                        </span>
                      )}
                      {j.employmentType && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wide"
                          style={{ background: 'var(--bg-3)', color: 'var(--text-mute)', border: '1px solid var(--line-2)' }}>
                          {t(`jobs_et_${j.employmentType.toLowerCase()}`)}
                        </span>
                      )}
                    </div>

                    {/* Licenses + code95 */}
                    {(j.licenses.length > 0 || j.code95) && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {j.licenses.map((lic) => (
                          <span key={lic} className="px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide"
                            style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)', border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)' }}>
                            <Truck size={10} className="inline mr-1" />{lic}
                          </span>
                        ))}
                        {j.code95 && (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide flex items-center gap-1"
                            style={{ background: 'var(--bg-4)', color: 'var(--text)', border: '1px solid var(--line-2)' }}>
                            <BadgeCheck size={10} /> Code 95
                          </span>
                        )}
                      </div>
                    )}

                    {/* Salary */}
                    {salaryText && (
                      <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-ink">
                        <Banknote size={14} style={{ color: 'var(--live)' }} />
                        <span>{salaryText}</span>
                      </div>
                    )}

                    {/* Start date */}
                    {j.startDate && (
                      <div className="flex items-center gap-1.5 mb-4 text-xs" style={{ color: 'var(--c-muted)' }}>
                        <CalendarClock size={12} />
                        <span>{t('jobs_start')}: {new Date(j.startDate).toLocaleDateString()}</span>
                      </div>
                    )}

                    {/* CTA */}
                    <div className="pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--c-border)' }}>
                      <span className="text-xs" style={{ color: 'var(--c-muted)' }}>
                        {j.publishedAt ? new Date(j.publishedAt).toLocaleDateString() : ''}
                      </span>
                      <Link href="/login"
                        className="text-sm font-semibold transition-colors hover:text-ink"
                        style={{ color: 'var(--c-accent)' }}>
                        {t('jobs_apply')} &rarr;
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-ghost flex items-center gap-1 text-sm px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} /> {t('companies_prev')}
                </button>
                <span className="px-4 text-sm" style={{ color: 'var(--c-muted)' }}>
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-ghost flex items-center gap-1 text-sm px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {t('companies_next')} <ChevronRight size={16} />
                </button>
              </div>
            )}

            {/* Schema.org JobPosting structured data — for Google Jobs rich results */}
            {jobPostingSchema && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingSchema) }}
              />
            )}
          </>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
